import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import NavigationStateManager from '../../helpers/NavigationStateManager';

import { pageTypesStrings } from '../../constants/pageTypes';
import { toCurrency } from '../../helpers/converterHelper';
import { setQueryParamIfAbsent } from '../../helpers/queryHelper';

import { ReadOnlyContext } from '../common/grid/ReadOnlyContext';

interface IViewCardSectionProps {
  type: pageTypesStrings;
  data?: {
    id: string | number;
    name: string;
    amount: string | number;
    urlFull: string;
    currency?: string;
  };
  imageUrl?: string;
  hideId?: boolean;
  /** An extra line under the name, such as a system-only organisation pill. */
  detail?: React.ReactNode;
  className?: string;
  isSystemAdmin?: boolean; // optional override; if not provided we read from store
}

export const ViewIconItem = ({
  type,
  data,
  imageUrl,
  hideId,
  detail,
  className,
  isSystemAdmin: isSystemAdminProp
}: IViewCardSectionProps) => {
  const globalIsSystemAdmin = useSelector((s: any) => s?.session?.isSystemAdmin);
  const isSystemAdmin = typeof isSystemAdminProp === 'boolean' ? isSystemAdminProp : globalIsSystemAdmin;
  const handleEditClick = (e: React.MouseEvent, id: string | number) => {
    e.preventDefault();
    const currentUrl = NavigationStateManager.getCurrentPageUrl();
    NavigationStateManager.setReturnUrl(currentUrl);
    
    // Navigate to edit URL
    const url = getUrl(id);
    window.location.href = url;
  };

  const getUrl = (id: string | number) => {
    switch (type) {
      case pageTypesStrings.CAMPAIGN:
        return `/campaigns/edit/${id}/setup`;
      case pageTypesStrings.EVENT:
        return `/events/edit/${id}/setup`;
      case pageTypesStrings.DONATION_SUB:
        return `/recurring/detail/${id}/donations`;
      case pageTypesStrings.DONATION:
        return `/donations/view/${id}`;
      default:
        return `/campaigns/${type}/edit/${id}/page-detail`;
    }
  };

  const getAdminViewUrl = (id: string | number) => {
    switch (type) {
      case pageTypesStrings.FUNDRAISER:
        return `/fundraisers/view/${id}`;
      case pageTypesStrings.TEAM:
        return `/fundraisers/team/view/${id}`;
      case pageTypesStrings.COLLECTION:
        return `/fundraisers/collection/view/${id}`;
      case pageTypesStrings.CAMPAIGN:
        return `/campaigns/performance/${id}`;
      default:
        return null; // event keep existing pattern only
    }
  };

  const showAdminViewLink = data && getAdminViewUrl(data.id);
  const isReadOnly = useContext(ReadOnlyContext);

  return (
    <React.Fragment>
      {data && (
        <div className="view-details-content-row page">
          {imageUrl && (
            <img
              alt=""
              src={setQueryParamIfAbsent(imageUrl, 'w', 200)}
              className="view-icon-page-thumbnail"
            />
          )}
          <div className="page-content view-icon-item-page-content">
            <h4>{I18n.t(`donation.donation-view.donation-details.${type}`)}</h4>
            {data.name && (
              data.urlFull ? (
                <a href={data.urlFull} target="_blank" rel="noreferrer" className="view-icon-item-name-link">
                  {data.name}
                </a>
              ) : (
                <span className="view-icon-item-name-text">{data.name}</span>
              )
            )}
            {detail && <div className="view-icon-item-detail">{detail}</div>}
            {data && (() => {
              const actions: React.ReactNode[] = [];
              const hasPublicUrl = !!data.urlFull;
              if (hasPublicUrl) {
                actions.push(
                  <a
                    key="visit"
                    href={data.urlFull}
                    target="_blank"
                    rel="noreferrer"
                  >Visit</a>
                );
              }
              if (showAdminViewLink) {
                actions.push(
                  <a
                    key="viewDetails"
                    href={getAdminViewUrl(data.id) || undefined}
                  >View details</a>
                );
              }
              const isDonation = type === pageTypesStrings.DONATION;
              const isEvent = type === pageTypesStrings.EVENT;
              const showEditLink = isDonation || ((!isEvent || isSystemAdmin) && !isReadOnly);
              
              if (showEditLink) {
                actions.push(
                  <a
                    key="edit"
                    href={getUrl(data.id)}
                    onClick={(e) => handleEditClick(e, data.id)}
                  >{isDonation ? 'View' : 'Edit'}</a>
                );
              }
              if (!actions.length) return null;
              return (
                <div className="view-icon-item-inline-actions">
                  {actions.map((a, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <span className="view-icon-item-action-separator">|</span>}
                      {a}
                    </React.Fragment>
                  ))}
                </div>
              );
            })()}
            {data.amount && toCurrency(data.amount, undefined, data.currency)}
            {!hideId && (
              <span>
                ID: {data.id}
              </span>
            )}
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default ViewIconItem;
